import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

import {
  gray40,
  gray80,
  white,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularXl, regularMd } from '../../../constants/location';

export type DeleteItemData = {
  id: string;
  itemName: string;
  isChild: boolean;
  parentId?: string;
  listIndex?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onDelete: (data: DeleteItemData) => void;
  itemData: DeleteItemData | null;
  loading?: boolean;
  customMessage?: React.ReactNode; // カスタムメッセージ（指定しない場合はデフォルトメッセージ）
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  deleteButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  deleteButtonText: {
    ...boldMd,
    color: white,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '24px',
      width: '560px',
      maxWidth: '90vw',
      '@media screen and (max-width: 960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  message: {
    ...regularMd,
    marginBottom: '32px',
  },
};

const ItemDeleteModal = ({
  open,
  onClose,
  onDelete,
  itemData,
  loading = false,
  customMessage,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  const handleDelete = (): void => {
    if (!itemData) return;
    onDelete(itemData);
  };

  const defaultMessage = (
    <>
      削除すると元に戻すことはできません。
      <br />
      項目を削除してよろしいですか？
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>項目の削除</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.message}>{customMessage || defaultMessage}</Typography>
      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={loading}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.deleteButton} onClick={handleDelete} disabled={loading}>
          <Typography sx={styles.deleteButtonText}>{loading ? '削除中...' : '削除する'}</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default ItemDeleteModal;
